import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, ArrowUpRight } from 'lucide-react';
import Footer from '../components/Footer';

const FutureObservatoryProject = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  const galleryImages = [
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/h5woyx4l_606_980-7ABB4E07-894E-4D99-8876-9F2270395CF7-2.jpeg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/315grebw_606_980-007FED06-51F9-47D9-81EC-B183619DA01B-2.jpeg',
  ];

  const copyright = "© Felix Speller for the Design Museum";

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <div className="bg-dark min-h-screen">
      {/* Back Button */}
      <div className="fixed top-24 left-6 md:left-12 z-40">
        <Link 
          to="/#projects" 
          className="inline-flex items-center gap-2 text-cream hover:text-cream/70 transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="text-sm uppercase tracking-wider">Back</span>
        </Link>
      </div>

      {/* Title Section */}
      <section className="pt-32 pb-8 px-6 md:px-12">
        <div className="max-w-5xl mx-auto text-center">
          <h1 
            className="text-5xl md:text-7xl lg:text-8xl uppercase text-cream mb-4"
            style={{ fontFamily: 'Anton, sans-serif' }}
          >
            Future Observatory
          </h1>
          <p className="text-xl md:text-2xl text-cream">
            Design Museum
          </p>
        </div>
      </section>

      {/* Full-width Carousel Gallery */}
      <section className="py-8">
        <div className="relative w-full">
          {/* Main Image */}
          <div className="relative w-full flex justify-center" style={{ height: '70vh' }}>
            <div className="relative h-full">
              <img 
                src={galleryImages[currentImageIndex]} 
                alt={`Future Observatory ${currentImageIndex + 1}`}
                className="h-full w-auto object-contain"
              />
              
              {/* Copyright overlay - inside image */}
              <div className="absolute bottom-4 right-4 bg-black/60 px-3 py-1 rounded">
                <span className="text-sm text-cream">{copyright}</span>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button 
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-cream/20 hover:bg-cream/40 rounded-full flex items-center justify-center transition-colors"
            >
              <ChevronLeft size={28} className="text-cream" />
            </button>
            <button 
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-cream/20 hover:bg-cream/40 rounded-full flex items-center justify-center transition-colors"
            >
              <ChevronRight size={28} className="text-cream" />
            </button>
          </div>

          {/* Image Counter */}
          <div className="text-center py-4">
            <span className="text-cream text-sm">
              {currentImageIndex + 1} / {galleryImages.length}
            </span>
          </div>

          {/* Thumbnail Strip */}
          <div className="flex justify-center gap-2 px-6 overflow-x-auto pb-4">
            {galleryImages.map((image, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentImageIndex(idx)}
                className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden transition-all ${
                  idx === currentImageIndex ? 'ring-2 ring-cream' : 'opacity-50 hover:opacity-100'
                }`}
              >
                <img 
                  src={image} 
                  alt={`Thumbnail ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Text Content */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <p className="text-2xl md:text-3xl lg:text-4xl leading-relaxed text-cream mb-16" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            National exhibition dedicated to design research on the environmental crisis curated by Future Observatory, the Design Museum's national research programme for the green transition.
          </p>
          
          <p className="text-xl md:text-2xl leading-relaxed text-cream mb-8" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Multi-film installation working at the intersection of research, design, and public communication to translate complex environmental thinking into exhibition films.
          </p>
          
          {/* Project Services */}
          <h2 
            className="text-2xl md:text-3xl uppercase text-cream mb-6 mt-12"
            style={{ fontFamily: 'Anton, sans-serif' }}
          >
            Project Services
          </h2>
          <ul className="space-y-3 text-cream text-lg mb-8" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            <li>• Delivered end-to-end production across a four-film installation</li>
            <li>• Collaborated with Dr Julia King to shape narrative from research to screen</li>
            <li>• Developed films specifically for spatial, in-gallery experience</li>
            <li>• Crafted content to communicate environmental systems and future-facing design thinking to broad audiences</li>
            <li>• Managed production to meet the demands and standards of a national institution</li>
          </ul>

          {/* Output */}
          <h2 
            className="text-2xl md:text-3xl uppercase text-cream mb-6 mt-12"
            style={{ fontFamily: 'Anton, sans-serif' }}
          >
            Output
          </h2>
          <ul className="space-y-2 text-cream text-lg mb-12" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            <li>4 x short films, presented as a cohesive exhibition installation</li>
          </ul>

          {/* External Link */}
          <a 
            href="https://designmuseum.org/exhibitions/future-observatory-display"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-cream hover:text-cream/70 transition-colors text-lg underline"
          >
            Visit Design Museum Website
            <ArrowUpRight size={20} />
          </a>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-16 px-6 md:px-12 bg-dark">
        <div className="max-w-5xl mx-auto">
          <div className="aspect-video bg-dark">
            <video
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover rounded-lg"
            >
              <source src="https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/12va236f_DM_LOOP.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FutureObservatoryProject;
